import {Color} from '@benchling/nucleus-ui';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import * as PropTypes from 'prop-types';
import React from 'react';
import _ from 'underscore';

import getTestProps from 'test/lib/cypress/getTestProps';
import DataTestAttrs from 'test/lib/cypress/shared/DataTestAttrs';
import * as Colors from 'ui-library/Colors';
import {InputChangeEvent, InputFocusEvent} from 'util/Types';

import {Size, SIZES, TEXT_INPUT_TYPES, TextInputType} from './InputConstants';

export interface TextInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> {
  className?: string;
  // TODO(TypeScript): Make required once defaultProps are handled correctly by TypeScript.
  hasButton?: boolean;
  // TODO(TypeScript): Make required once defaultProps are handled correctly by TypeScript.
  hasError?: boolean;
  // TODO(TypeScript): Make required once defaultProps are handled correctly by TypeScript.
  size?: Size;
  // TODO(TypeScript): Make required once defaultProps are handled correctly by TypeScript.
  type?: TextInputType;
  autoFocus?: boolean;
  /**
   * If true, all the text in the input will be selected when the component mounts.
   */
  initialSelect?: boolean;
  onChange?: (e: InputChangeEvent) => void;
  onBlur?: (e: InputFocusEvent) => void;
  disabled?: boolean;
  readOnly?: boolean;
  maxLength?: number;
  dataTestAttrs?: DataTestAttrs;
}

export default class TextInput extends React.Component<TextInputProps> {
  static propTypes = {
    className: PropTypes.string,
    disabled: PropTypes.bool,
    hasButton: PropTypes.bool.isRequired,
    hasError: PropTypes.bool.isRequired,
    initialSelect: PropTypes.bool,
    size: PropTypes.oneOf(Object.values(SIZES)),
    type: PropTypes.oneOf(Object.values(TEXT_INPUT_TYPES)).isRequired,
    autoFocus: PropTypes.bool,
    maxLength: PropTypes.number,
  };

  inputRef = React.createRef<HTMLInputElement>();

  static defaultProps = {
    autoFocus: false,
    hasButton: false,
    hasError: false,
    initialSelect: false,
    type: TextInputType.TEXT,
  };

  componentDidMount(): void {
    if (this.props.initialSelect) {
      this.select();
    }
  }

  public focus(): void {
    this.inputRef.current!.focus();
  }

  public select(): void {
    this.inputRef.current!.select();
  }

  render(): JSX.Element {
    return (
      <input
        // eslint-disable-next-line no-restricted-syntax
        {..._.omit(this.props, 'hasButton', 'hasError', 'initialSelect', 'size', 'dataTestAttrs')}
        className={classNames(
          this.props.className,
          'fieldInput-input',
          this.props.readOnly && css(styles.readOnly),
          this.props.disabled && css(styles.disabled),
          {
            'fieldInput-input--hasButton': this.props.hasButton,
            'fieldInput-input--error': this.props.hasError,
            'fieldInput-input--small': this.props.size === SIZES.SMALL,
            'fieldInput-input--medium': this.props.size === SIZES.MEDIUM,
          }
        )}
        type={this.props.type}
        autoFocus={this.props.autoFocus}
        onBlur={this.props.onBlur}
        maxLength={this.props.maxLength}
        ref={this.inputRef}
        {...getTestProps(this.props.dataTestAttrs)}
      />
    );
  }
}

const styles = StyleSheet.create({
  disabled: {
    backgroundColor: Color.GRAY_100,
    borderColor: Color.GRAY_100,
    // Set on color and placeholder color so the text is the right color for placeholders and values
    color: Color.GRAY_600,
    '::placeholder': {
      color: Color.GRAY_600,
    },
  },
  readOnly: {
    // This overrides the default CSS styling for readonly tags, which is to add a gray background
    background: Colors.WHITE,
  },
});
